import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import './index.css';
import axios from '../../store/axios';

function OAuthConsent() {
  const history = useHistory();
  const location = useLocation();
  const { auth } = useSelector(state => state['Auth']);
  const { currentBranding } = useSelector(state => state['Settings']);

  const [ui, setUi] = useState({ loading: true, deciding: false, error: null, client: null, agreed: false });
  const params = useMemo(() => Object.fromEntries(new URLSearchParams(location['search']).entries()), [location]);
  const agencyName = currentBranding?.['agencyName'] || 'Sell Agent';

  useEffect(() => { document.title = 'Authorize | ' + agencyName }, [currentBranding]);

  // Not logged in → stash the OAuth request and continue via the normal login/signup pages
  useEffect(() => {
    if (!auth) {
      localStorage.setItem('oauthPending', location['search']);
      history.push('/login');
    }
  }, [auth]);

  // Validate the authorization request before showing the consent card
  // Reason: re-run whenever location.search changes too — React Router reuses this component instance
  // across same-route navigations, so a second authorize attempt in the same tab must not get stuck
  // showing the previous attempt's stale loading/error/client state.
  useEffect(() => {
    if (!auth) return;
    // Reason: do NOT clear oauthPending here — a stray login/signup submission can still be in flight
    // (e.g. an already-authenticated tab where the user re-submits the form) and its saga redirect
    // depends on oauthPending still being set. Only clear it once the user actually decides (see decide()).
    setUi(prev => ({ ...prev, loading: true, error: null, client: null, agreed: false }));
    axios.get(`/user/oauth/authorize/validate${location['search']}`)
      .then(({ data }) => setUi(prev => ({ ...prev, loading: false, client: data['body'] })))
      .catch(err => setUi(prev => ({ ...prev, loading: false, error: err['response']?.['data']?.['message'] || 'Invalid authorization request' })));
  }, [auth, location['search']]);

  const decide = (approve) => {
    setUi(prev => ({ ...prev, deciding: true }));
    axios.post('/user/oauth/authorize/decision', { ...params, approve })
      .then(({ data }) => {
        localStorage.removeItem('oauthPending');
        window.location.href = data['body']['redirectTo'];
      })
      .catch(err => setUi(prev => ({ ...prev, deciding: false, error: err['response']?.['data']?.['message'] || 'Authorization failed' })));
  };

  const clientName = ui['client']?.['clientName'] || 'Claude';

  return (
    <div className='oauth-consent-page'>
      <div className='oauth-consent-card'>
        <div className='oauth-consent-logo-area'>
          {currentBranding?.['logo'] && <img className='oauth-consent-logo' src={currentBranding['logo']} alt={agencyName} />}
        </div>

        {ui['loading'] && <p className='oauth-consent-loading'><i className='fa fa-spinner fa-spin' /> Checking authorization request…</p>}

        {!ui['loading'] && ui['error'] && (
          <>
            <h2 className='oauth-consent-title'>Authorization Error</h2>
            <p className='oauth-consent-error'>{ui['error']}</p>
            <p className='oauth-consent-hint'>Return to Claude and click Connect to try again.</p>
          </>
        )}

        {!ui['loading'] && !ui['error'] && (
          <>
            <p className='oauth-consent-eyebrow'>Authorization Request</p>
            <h2 className='oauth-consent-title'>{clientName} wants to access your {agencyName} account</h2>
            <p className='oauth-consent-subtitle'>This connects {clientName} to {agencyName} so it can act on your behalf inside your account.</p>

            <ul className='oauth-consent-scopes'>
              {CONNECTOR_SCOPES.map((scope, i) => (
                <li key={i} className='oauth-consent-scope-item'>
                  <i className='fa fa-check-circle' />
                  <span className='oauth-consent-scope-text'>
                    <span className='oauth-consent-scope-title'>{scope['title']}</span>
                    <span className='oauth-consent-scope-desc'>{scope['description']}</span>
                  </span>
                </li>
              ))}
            </ul>

            <label className='oauth-consent-agree'>
              <input type='checkbox' checked={ui['agreed']} onChange={e => setUi(prev => ({ ...prev, agreed: e['target']['checked'] }))} />
              I agree to {agencyName}'s <a href='/terms' target='_blank' rel='noreferrer'>Terms of Service</a> and <a href='/privacy' target='_blank' rel='noreferrer'>Privacy Policy</a>.
            </label>

            <div className='oauth-consent-actions'>
              <button className='btn-style-border' disabled={ui['deciding']} onClick={() => decide(false)}>Deny</button>
              <button className='btn-style-new' disabled={ui['deciding'] || !ui['agreed']} onClick={() => decide(true)}>
                {ui['deciding'] ? <i className='fa fa-spinner fa-spin' /> : 'Authorize'}
              </button>
            </div>

            <div className='oauth-consent-footer'>
              <i className='fa fa-shield' />
              <span>You're signing in to a trusted app. Revoke access anytime from Settings → Security → Log out all devices.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CONNECTOR_SCOPES = [
  { title: 'Get 100 free leads per day', description: 'Pull fresh, verified leads into your workspace daily.' },
  { title: 'Connect your mailbox and manage warmup', description: 'Read sending health and adjust warmup settings.' },
  { title: 'Create and launch campaigns', description: 'Build outbound campaigns and start sends for you.' },
];

export default OAuthConsent;
